import React from 'react';
import DOMPurify from 'dompurify';
import { useQuery } from '@tanstack/react-query'; //-tech-debt-react-query;
import { moodleFetch } from '../../../../hooks/useMoodleHooks';
import { useMoodleUi } from '../MoodleUiContext';

interface Props {
  lessonid: number;
  pageid: number;
  jumps: number[];
  onJump: (pageid: number) => void;
}

const MoodleLessonPageAnswers: React.FC<Props> = ({ lessonid, pageid, jumps, onJump }) => {
  const { config } = useMoodleUi();
  const instance = config.instance;

  const { data, isLoading, error } = useQuery({
    queryKey: ['lesson-page-answers', lessonid, pageid, instance],
    queryFn: async () => {
      return moodleFetch<any>(
        `moodle/lesson/${lessonid}/page/${pageid}/answers/`,
        {},
        {},
        instance
      );
    },
    ...{ enabled: !!lessonid && !!pageid },
  });

  if (isLoading) {
    return (
      <p
        style={{
          fontFamily: 'var(--hbr-font-body)',
          fontSize: 'var(--hbr-font-size-md)',
          lineHeight: 'var(--hbr-font-line-height-normal)',
        }}
      >
        Loading options…
      </p>
    );
  }

  if (error) {
    return (
      <p
        role="alert"
        style={{
          color: 'red',
          fontFamily: 'var(--hbr-font-body)',
          fontSize: 'var(--hbr-font-size-md)',
          lineHeight: 'var(--hbr-font-line-height-normal)',
        }}
      >
        {String(error)}
      </p>
    );
  }

  if (!data?.answers?.length) return null;

  return (
    <div className="answers">
      <div className="answers-title">Choose an option:</div>
      <div className="answers-list">
        {data.answers.map((ans: any, i: number) => (
          <button
            key={ans.id}
            type="button"
            className="opt-btn"
            onClick={() => onJump(jumps[i])}
            aria-label={`Choose option ${i + 1}`}
          >
            <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(ans.answer) }} />
          </button>
        ))}
      </div>

      <style>
        {`
          .answers {
            margin-top: var(--hbr-spacing-lg);
          }

          .answers-title {
            font-family: var(--hbr-font-body);
            font-size: var(--hbr-font-size-sm);
            font-weight: var(--hbr-font-weight-semibold);
          }

          .answers-list {
            display: flex;
            flex-direction: column;
            gap: var(--hbr-spacing-sm);
            margin-top: var(--hbr-spacing-xs);
          }

          .opt-btn {
            width: 100%;
            display: inline-flex;
            align-items: center;
            justify-content: flex-start;
            gap: var(--hbr-spacing-xs);
            padding: 0.8rem 1rem;
            border-radius: 0.6rem;
            border: 1px solid rgba(0,0,0,0.2);
            background: #fff;
            color: inherit;
            cursor: pointer;
            text-align: left;
            font-family: var(--hbr-font-body);
            font-size: var(--hbr-font-size-md);
            line-height: var(--hbr-font-line-height-normal);
            transition: background 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
          }

          .opt-btn:hover {
            background: rgba(10,102,255,0.06);
            border-color: rgba(10,102,255,0.5);
          }

          .opt-btn:active {
            background: rgba(10,102,255,0.12);
          }

          .opt-btn:focus {
            outline: none;
            box-shadow: 0 0 0 3px rgba(10,102,255,0.35);
          }
        `}
      </style>
    </div>
  );
};

export default MoodleLessonPageAnswers;